import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Me } from '@4zen/api-client';
import { api, http, onUnauthorized, tokenStore } from '@/lib/api';

interface AuthState {
  user: Me | null;
  loading: boolean;
  login: (input: {
    email: string;
    password: string;
    org_slug?: string;
    totp_code?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  can: (permission: string) => boolean;
  hasFeature: (feature: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (!tokenStore.read()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.auth.me());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    onUnauthorized(() => {
      http.setTokens(null);
      setUser(null);
    });
    void refreshUser();
  }, [refreshUser]);

  const login = useCallback<AuthState['login']>(
    async (input) => {
      const tokens = await api.auth.login({
        ...input,
        platform: '__TAURI_INTERNALS__' in globalThis ? 'desktop' : 'web',
        device_name: navigator.userAgent.slice(0, 120),
      });
      http.setTokens(tokens);
      setUser(await api.auth.me());
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      http.setTokens(null);
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      logout,
      refreshUser,
      can: (permission) => user?.permissions.includes(permission) ?? false,
      hasFeature: (feature) => user?.features.includes(feature) ?? false,
    }),
    [user, loading, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
